import errorMessages from '@common/constants/errorMessages';
import {
  isAllDigits,
  isEmpty,
  notEmpty,
  validateEmployerIdentificationNumber,
  validateFullSocialSecurityNumber,
  validatePhoneNumber,
  validateZipCode,
} from '@common/paymentsUtils/inputValidator';

const BIRTHDAY_MIN = 1;
const BIRTHDAY_MAX = 31;

function isRequiredFieldEmpty(value, errorMessage) {
  if (isEmpty(value)) {
    return errorMessage;
  }
  return errorMessages.CLEAR_ERROR;
}

function isZipCodeValid(zipCode) {
  if (isEmpty(zipCode)) {
    return errorMessages.ZIP_CODE_EMPTY;
  } else if (zipCode.length !== 5) {
    return errorMessages.ZIP_CODE_INVALID;
  } else if (!validateZipCode(zipCode)) {
    return errorMessages.ZIP_CODE_INVALID;
  }
  return errorMessages.CLEAR_ERROR;
}

function isPhoneNumberValid(phoneNumber) {
  if (!validatePhoneNumber(phoneNumber)) {
    return errorMessages.PHONE_NUMBER_INVALID;
  }
  return errorMessages.CLEAR_ERROR;
}

function isDayOfBirthValid(dayOfBirth) {
  if (isEmpty(dayOfBirth)) {
    return errorMessages.DAY_OF_BIRTH_EMPTY;
  } else if (!isAllDigits(dayOfBirth)) {
    return errorMessages.DAY_OF_BIRTH_INVALID;
  } else if (dayOfBirth < BIRTHDAY_MIN) {
    return errorMessages.DAY_OF_BIRTH_INVALID;
  } else if (dayOfBirth > BIRTHDAY_MAX) {
    return errorMessages.DAY_OF_BIRTH_INVALID;
  }
  return errorMessages.CLEAR_ERROR;
}

function isYearOfBirthValid(yearOfBirth, minYear, maxYear) {
  if (isEmpty(yearOfBirth)) {
    return errorMessages.YEAR_OF_BIRTH_EMPTY;
  } else if (!isAllDigits(yearOfBirth)) {
    return errorMessages.YEAR_OF_BIRTH_INVALID;
  } else if (yearOfBirth < minYear) {
    return errorMessages.YEAR_OF_BIRTH_INVALID;
  } else if (yearOfBirth > maxYear) {
    return errorMessages.BIRTH_DATE_INVALID;
  }
  return errorMessages.CLEAR_ERROR;
}

function isFullSocialSecurityNumberValid(fullSocialSecurityNumber) {
  if (!validateFullSocialSecurityNumber(fullSocialSecurityNumber)) {
    return errorMessages.FULL_SSN_INVALID;
  }
  return errorMessages.CLEAR_ERROR;
}

// To validate if user is 18+
function isDateOfBirthValid(monthOfBirth, dayOfBirth, yearOfBirth, maxBirthDate) {
  const maxMonth = maxBirthDate.getUTCMonth() + 1;
  const maxDay = maxBirthDate.getUTCDate();
  const maxYear = maxBirthDate.getUTCFullYear();

  if (notEmpty(yearOfBirth) && notEmpty(dayOfBirth) && notEmpty(monthOfBirth)) {
    if (Number(yearOfBirth) === maxYear) {
      if (monthOfBirth > maxMonth) {
        return errorMessages.BIRTH_DATE_INVALID;
      } else if (Number(monthOfBirth) === maxMonth) {
        if (dayOfBirth > maxDay) {
          return errorMessages.BIRTH_DATE_INVALID;
        }
      }
    }
  }
  return errorMessages.CLEAR_ERROR;
}

function isEmployerIdentificationNumberValid(employerIdentificationNumber) {
  if (!validateEmployerIdentificationNumber(employerIdentificationNumber)) {
    return errorMessages.EMPLOYER_IDENTIFICATION_NUMBER_INVALID;
  }
  return errorMessages.CLEAR_ERROR;
}
export function validateAllInputs(ctx) {
  const {
    firstName,
    lastName,
    address,
    city,
    state,
    zip,
    phoneNumber,
    dobMonth,
    dobDay,
    dobYear,
    ssn,
    companyName,
    companyTaxId,
    companyAddress,
    companyCity,
    companyState,
    companyZip,
  } = ctx.state;

  const { landlordBirthDayMax, landlordBirthDayMin, selectedOption } = ctx.props;

  const maxBirthDate = new Date(landlordBirthDayMax);
  const minBirthDate = new Date(landlordBirthDayMin);

  const errors: Array<Record<string, unknown>> = [];
  const firstNameError = isRequiredFieldEmpty(firstName.value, errorMessages.LEGAL_FIRST_NAME_EMPTY);
  const lastNameError = isRequiredFieldEmpty(lastName.value, errorMessages.LAST_NAME_EMPTY);
  const addressError = isRequiredFieldEmpty(address.value, errorMessages.STREET_EMPTY);
  const cityError = isRequiredFieldEmpty(city.value, errorMessages.CITY_EMPTY);
  const stateError = isRequiredFieldEmpty(state.value, errorMessages.STATE_EMPTY);
  const zipError = isZipCodeValid(zip.value);
  const phoneNumberError = isPhoneNumberValid(phoneNumber.value);
  const dobMonthError = isRequiredFieldEmpty(dobMonth.value, errorMessages.MONTH_OF_BIRTH_EMPTY);
  const dobDayError = isDayOfBirthValid(dobDay.value);
  const dobYearError = isYearOfBirthValid(dobYear.value, minBirthDate.getUTCFullYear(), maxBirthDate.getUTCFullYear());
  const ssnError = isFullSocialSecurityNumberValid(ssn.value);
  const dateOfBirthError = isDateOfBirthValid(dobMonth.value, dobDay.value, dobYear.value, maxBirthDate);
  const dobYearCommonError = dobYearError || dateOfBirthError || '';

  if (firstNameError) {
    errors.push({ firstName: firstNameError });
  }

  if (lastNameError) {
    errors.push({ lastName: lastNameError });
  }

  if (addressError) {
    errors.push({ address: addressError });
  }

  if (cityError) {
    errors.push({ city: cityError });
  }

  if (stateError) {
    errors.push({ state: stateError });
  }

  if (zipError) {
    errors.push({ zip: zipError });
  }

  if (phoneNumberError) {
    errors.push({ phoneNumber: phoneNumberError });
  }

  if (dobMonthError) {
    errors.push({ dobMonth: dobMonthError });
  }

  if (dobDayError) {
    errors.push({ dobDay: dobDayError });
  }

  if (dobYearCommonError) {
    errors.push({ dobYear: dobYearCommonError });
  }

  if (ssnError) {
    errors.push({ ssn: ssnError });
  }

  let businessFields: Record<string, { value: string; error: string }> = {};

  if (selectedOption === 'single_member_llc' || selectedOption === 'multi_member_llc') {
    const companyNameError = isRequiredFieldEmpty(companyName.value, errorMessages.BUSINESS_NAME_EMPTY);
    if (companyNameError) {
      errors.push({ companyName: companyNameError });
    }
    const companyTaxIdError = isEmployerIdentificationNumberValid(companyTaxId.value);
    if (companyTaxIdError) {
      errors.push({ companyTaxId: companyTaxIdError });
    }

    businessFields = {
      companyName: { ...companyName, error: companyNameError },
      companyTaxId: { ...companyTaxId, error: companyTaxIdError },
    };

    if (selectedOption === 'multi_member_llc') {
      const companyAddressError = isRequiredFieldEmpty(companyAddress.value, errorMessages.BUSINESS_STREET_EMPTY);
      if (companyAddressError) {
        errors.push({ companyAddress: companyAddressError });
      }
      const companyCityError = isRequiredFieldEmpty(companyCity.value, errorMessages.BUSINESS_CITY_EMPTY);
      if (companyCityError) {
        errors.push({ companyCity: companyCityError });
      }
      const companyStateError = isRequiredFieldEmpty(companyState.value, errorMessages.BUSINESS_STATE_EMPTY);
      if (companyStateError) {
        errors.push({ companyState: companyStateError });
      }
      const companyZipError = isZipCodeValid(companyZip.value);
      if (companyZipError) {
        errors.push({ companyZip: companyZipError });
      }
      businessFields = {
        ...businessFields,
        companyAddress: { ...companyAddress, error: companyAddressError },
        companyCity: { ...companyCity, error: companyCityError },
        companyState: { ...companyState, error: companyStateError },
        companyZip: { ...companyZip, error: companyZipError },
      };
    }
  }

  ctx.setIdVerificationInfo({
    ...ctx.state,
    firstName: { ...firstName, error: firstNameError },
    lastName: { ...lastName, error: lastNameError },
    address: { ...address, error: addressError },
    city: { ...city, error: cityError },
    state: { ...state, error: stateError },
    zip: { ...zip, error: zipError },
    phoneNumber: { ...phoneNumber, error: phoneNumberError },
    dobMonth: { ...dobMonth, error: dobMonthError },
    dobDay: { ...dobDay, error: dobDayError },
    dobYear: { ...dobYear, error: dobYearCommonError },
    ssn: { ...ssn, error: ssnError },
    ...businessFields,
  });

  if (errors.length > 0) {
    return false;
  }
  return true;
}
